import { Fragment, useMemo, useState, type FormEvent } from "react";
import { useApi } from "../useApi.js";
import { api, dateDe } from "../api.js";
import type { CertificationRow, CrewRow, DeviceRow, DeviceType, Me, ServiceCatalogEntry } from "../types.js";
import { DEVICE_TYPES } from "../types.js";
import { Page, Pill } from "../components/Shell.js";
import { TableSkeleton } from "../components/Skeleton.js";
import ConfirmDeleteButton from "../components/ConfirmDeleteButton.js";
import { toast } from "../toast.js";

const emptyForm = {
  name: "", deviceType: DEVICE_TYPES[0] as DeviceType, trainingCode: "",
  validFrom: "", validUntil: "", unlimited: false,
  crewName: "", deviceId: "",
};

function NewCertForm({ crews, devices, trainings, prefill, onCreated, onCancelPrefill }: {
  crews: CrewRow[]; devices: DeviceRow[]; trainings: ServiceCatalogEntry[];
  prefill?: CertificationRow | null; onCreated: () => Promise<void>; onCancelPrefill?: () => void;
}) {
  const [form, setForm] = useState(() => prefill
    ? {
        name: prefill.name, deviceType: prefill.deviceType,
        trainingCode: prefill.trainingCode ?? (trainings[0]?.code ?? ""),
        validFrom: "", validUntil: "", unlimited: false,
        crewName: prefill.crewName ?? "", deviceId: prefill.deviceId ?? "",
      }
    : { ...emptyForm, trainingCode: trainings[0]?.code ?? "" });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const selectedTraining = trainings.find((t) => t.code === form.trainingCode);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) { setErr("Bitte eine Datei auswählen."); return; }
    if (!form.trainingCode) { setErr("Bitte ein Schulungsprogramm auswählen."); return; }
    setBusy(true);
    setErr(null);
    try {
      await api.uploadForm("/api/certifications", {
        name: form.name, deviceType: form.deviceType, trainingCode: form.trainingCode,
        validFrom: form.validFrom,
        validUntil: form.unlimited ? undefined : (form.validUntil || undefined),
        crewName: form.crewName || undefined, deviceId: form.deviceId || undefined,
        file,
      });
      setForm({ ...emptyForm, trainingCode: trainings[0]?.code ?? "" });
      setFile(null);
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {prefill && (
        <div className="pill info" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "8px 12px" }}>
          <span style={{ flex: 1 }}>
            Erneuerung für <b>{prefill.name}</b>
            {prefill.training && <> · bisher {prefill.training.name}</>}
            {prefill.validUntil ? <> · lief bis {dateDe(prefill.validUntil)}</> : <> · bisher unbefristet</>}
          </span>
          {onCancelPrefill && (
            <button type="button" className="link-button" style={{ background: "none" }} onClick={onCancelPrefill}>
              stattdessen neu anlegen
            </button>
          )}
        </div>
      )}
      {err && <div className="err" role="alert">{err}</div>}
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "14px 18px" }}>
        <div className="field" style={{ gridColumn: "span 2" }}>
          <label htmlFor="cf-name">Name</label>
          <input id="cf-name" required placeholder="z. B. Max Mustermann"
                 value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn: "span 2" }}>
          <label htmlFor="cf-training">Schulungsprogramm</label>
          <select id="cf-training" required value={form.trainingCode}
                  onChange={(e) => set("trainingCode", e.target.value)}
                  style={{ width: "100%", padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            {trainings.length === 0 && <option value="">— keine Schulung im Katalog —</option>}
            {trainings.map((t) => (
              <option key={t.code} value={t.code}>
                Stufe {t.code} – {t.name}{t.duration ? ` (${t.duration})` : ""}
              </option>
            ))}
          </select>
          {selectedTraining && (
            <div className="sub-t" style={{ marginTop: 7 }}>
              {selectedTraining.description}
              {selectedTraining.credential && <> — <b>{selectedTraining.credential}</b></>}
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="cf-type">Gerätetyp</label>
          <select id="cf-type" value={form.deviceType}
                  onChange={(e) => set("deviceType", e.target.value as DeviceType)}
                  style={{ width: "100%", padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            {DEVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cf-crew">Kolonne (optional)</label>
          <select id="cf-crew" value={form.crewName} onChange={(e) => set("crewName", e.target.value)}
                  style={{ width: "100%", padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            <option value="">— keine —</option>
            {crews.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cf-from">Gültig ab</label>
          <input id="cf-from" type="date" required
                 value={form.validFrom} onChange={(e) => set("validFrom", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cf-until">Gültig bis</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input id="cf-until" type="date" disabled={form.unlimited} style={{ flex: 1 }}
                   value={form.validUntil} onChange={(e) => set("validUntil", e.target.value)} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, fontSize: 12.5, color: "var(--ink-2)" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={form.unlimited}
                   onChange={(e) => set("unlimited", e.target.checked)} />
            Unbefristet gültig
          </label>
        </div>
        <div className="field">
          <label htmlFor="cf-device">Gerät (optional)</label>
          <select id="cf-device" value={form.deviceId} onChange={(e) => set("deviceId", e.target.value)}
                  style={{ width: "100%", padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            <option value="">— keins —</option>
            {devices.map((d) => <option key={d.id} value={d.id}>{d.inventoryNo} — {d.crew}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: "span 2" }}>
          <label htmlFor="cf-file">Zertifikat (PDF, PNG, JPEG oder WebP)</label>
          <input id="cf-file" type="file" required accept="application/pdf,image/png,image/jpeg,image/webp"
                 onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                 style={{ padding: "7px 0" }} />
        </div>
      </div>
      <button className="btn pri" type="submit" disabled={busy} style={{ marginTop: 14 }}>
        {busy ? "Wird angelegt…" : prefill ? "Erneuertes Zertifikat speichern" : "Zertifikat anlegen"}
      </button>
    </form>
  );
}

/** Zertifikate derselben Person + desselben Programms: nur das neueste zählt als aktuell. */
function computeSuperseded(rows: CertificationRow[]): Set<string> {
  const latest = new Map<string, CertificationRow>();
  for (const c of rows) {
    const key = `${c.name}::${c.trainingCode ?? ""}`;
    const cur = latest.get(key);
    if (!cur || new Date(c.validFrom) > new Date(cur.validFrom)) latest.set(key, c);
  }
  const superseded = new Set<string>();
  for (const c of rows) {
    const key = `${c.name}::${c.trainingCode ?? ""}`;
    if (latest.get(key)?.id !== c.id) superseded.add(c.id);
  }
  return superseded;
}

/** Ein Zertifikat einer Person – für die aufgeklappte Detailliste. */
function CertDetailRow({ c, isOld, isAdmin, onRenew, onDelete }: {
  c: CertificationRow; isOld: boolean; isAdmin: boolean;
  onRenew: () => void; onDelete: () => Promise<void>;
}) {
  const expired = c.validUntil !== null && new Date(c.validUntil).getTime() < Date.now();
  return (
    <tr style={isOld ? { opacity: 0.6 } : undefined}>
      <td>
        {c.training ? <>
          <b>{c.training.name}</b>
          <div className="sub-t">Stufe {c.training.code}</div>
        </> : <span className="sub-t">—</span>}
      </td>
      <td className="mono" style={{ fontSize: 12.5 }}>{c.deviceType}</td>
      <td className="num">{dateDe(c.validFrom)}</td>
      <td className="num">
        {c.validUntil ? dateDe(c.validUntil) : <span className="sub-t">unbefristet</span>}
        <div style={{ display: "flex", gap: 6, marginTop: 2, justifyContent: "flex-end" }}>
          {isOld && <Pill status="mute">Erneuert</Pill>}
          {expired && !isOld && <Pill status="CRITICAL">Abgelaufen</Pill>}
        </div>
      </td>
      <td>
        {c.crewName && <span className="pill mute" style={{ marginRight: 6 }}>{c.crewName}</span>}
        {c.device && <span className="pill info mono">{c.device.inventoryNo}</span>}
        {!c.crewName && !c.device && <span className="sub-t">—</span>}
      </td>
      <td>
        <a href={c.filePath} target="_blank" rel="noreferrer" className="mono" style={{ fontSize: 12.5 }}>
          {c.fileName}
        </a>
      </td>
      {isAdmin && (
        <td className="r" style={{ whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-flex", gap: 6 }}>
            <button className="btn ghost sm" onClick={onRenew}>Erneuern</button>
            <ConfirmDeleteButton onConfirm={onDelete} />
          </span>
        </td>
      )}
    </tr>
  );
}

interface PersonGroup { name: string; certs: CertificationRow[] }

export default function Certifications({ me }: { me: Me }) {
  const { data, error, loading, reload } = useApi<CertificationRow[]>("/api/certifications");
  const crewsApi = useApi<CrewRow[]>("/api/crews");
  const devicesApi = useApi<DeviceRow[]>("/api/devices");
  const catalogApi = useApi<ServiceCatalogEntry[]>("/api/service/catalog");
  const [open, setOpen] = useState(false);
  const [renewFrom, setRenewFrom] = useState<CertificationRow | null>(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const trainings = useMemo(
    () => (catalogApi.data ?? []).filter((s) => s.kind === "SCHULUNG"),
    [catalogApi.data],
  );

  const superseded = useMemo(() => computeSuperseded(data ?? []), [data]);

  const list = (data ?? []).filter(
    (c) => !q || c.name.toLowerCase().includes(q.toLowerCase()),
  );

  const groups: PersonGroup[] = useMemo(() => {
    const map = new Map<string, CertificationRow[]>();
    for (const c of list) map.set(c.name, [...(map.get(c.name) ?? []), c]);
    return [...map.entries()]
      .map(([name, certs]) => ({
        name,
        certs: certs.slice().sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()),
      }))
      .sort((a, b) => new Date(b.certs[0]!.validFrom).getTime() - new Date(a.certs[0]!.validFrom).getTime());
  }, [list]);

  function toggle(name: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  function startRenew(c: CertificationRow) {
    setRenewFrom(c);
    setOpen(true);
  }

  async function remove(id: string) {
    try {
      await api.delete(`/api/certifications/${id}`);
      await reload();
      toast("Zertifikat gelöscht", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Löschen fehlgeschlagen", "error");
    }
  }

  return (
    <Page title="Zertifizierungen" sub={data ? `${data.length} Zertifikate` : undefined}
          actions={me.role === "ADMIN" && (
            <button className="btn pri" onClick={() => { setRenewFrom(null); setOpen((o) => !o); }}>
              {open ? "Abbrechen" : "Zertifikat anlegen"}
            </button>
          )}>

      {open && (
        <div className="card" style={{ marginBottom: 16, maxWidth: 560 }}>
          <div className="card-h"><h3>{renewFrom ? "Zertifikat erneuern" : "Neues Zertifikat"}</h3></div>
          <div className="card-b">
            <NewCertForm key={renewFrom?.id ?? "new"}
                         crews={crewsApi.data ?? []} devices={devicesApi.data ?? []} trainings={trainings}
                         prefill={renewFrom}
                         onCancelPrefill={() => setRenewFrom(null)}
                         onCreated={async () => {
                           setOpen(false); setRenewFrom(null); await reload();
                           toast(renewFrom ? "Zertifikat erneuert" : "Zertifikat angelegt", "success");
                         }} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-h">
          <h3>Alle Zertifikate</h3>
          <span className="hint">Name, Schulung und Gültigkeitszeitraum – optional an Kolonne oder Gerät gebunden</span>
          <div className="spacer" />
          <input className="srch" placeholder="Name suchen – Historie einer Person…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {loading && <TableSkeleton widths={[16, 18, 12, 12, 12, 12]} />}
        {error && <div className="empty">{error}</div>}
        {data && data.length === 0 && <div className="empty">Noch kein Zertifikat hinterlegt.</div>}
        {data && data.length > 0 && list.length === 0 && (
          <div className="empty">Kein Zertifikat für „{q}".</div>
        )}
        {groups.length > 0 && (
          <div className="tbl-wrap"><table>
            <thead><tr>
              <th>Name</th><th className="r">Zertifikate</th><th>Zuletzt</th><th>Status</th><th />
            </tr></thead>
            <tbody>
              {groups.map((g) => {
                const isOpen = expanded.has(g.name);
                const latest = g.certs[0]!;
                const anyExpiredCurrent = g.certs.some(
                  (c) => !superseded.has(c.id) && c.validUntil !== null && new Date(c.validUntil).getTime() < Date.now(),
                );
                return (
                  <Fragment key={g.name}>
                    <tr className="click" onClick={() => toggle(g.name)}>
                      <td><b>{g.name}</b></td>
                      <td className="r num">{g.certs.length}</td>
                      <td>
                        {latest.training?.name ?? <span className="sub-t">—</span>}
                        <div className="sub-t">{dateDe(latest.validFrom)}</div>
                      </td>
                      <td>
                        {anyExpiredCurrent
                          ? <Pill status="CRITICAL">Abgelaufen</Pill>
                          : <Pill status="OK">Aktuell</Pill>}
                      </td>
                      <td className="r sub-t">{isOpen ? "▲ Einklappen" : "▼ Öffnen"}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, background: "var(--surface-2)" }}>
                          <table>
                            <thead><tr>
                              <th>Schulung</th><th>Gerätetyp</th><th>Gültig ab</th><th>Gültig bis</th>
                              <th>Verknüpfung</th><th>Datei</th>
                              {me.role === "ADMIN" && <th />}
                            </tr></thead>
                            <tbody>
                              {g.certs.map((c) => (
                                <CertDetailRow key={c.id} c={c} isOld={superseded.has(c.id)}
                                               isAdmin={me.role === "ADMIN"}
                                               onRenew={() => startRenew(c)}
                                               onDelete={() => remove(c.id)} />
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </Page>
  );
}
